import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Search, X, ChevronUp, ChevronDown, Trash2, GitMerge, Download, SlidersHorizontal } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import CustomerModal from '../components/customers/CustomerModal';
import CustomerMergeModal from '../components/customers/CustomerMergeModal';
import OrderModal from '../components/orders/OrderModal';
import ServiceCallModal from '../components/service_calls/ServiceCallModal';

const CUSTOMER_COLS = [
  { key: 'phone', label: 'טלפון' },
  { key: 'city', label: 'עיר' },
  { key: 'interests', label: 'תחומי עניין' },
  { key: 'orders', label: 'הזמנות' },
  { key: 'service_calls', label: 'קריאות שירות' },
  { key: 'calls_count', label: 'שיחות' },
  { key: 'created_at', label: 'תאריך הצטרפות' },
];

export default function Customers() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);

  // Column visibility
  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [showColPicker, setShowColPicker] = useState(false);
  const colPickerRef = useRef(null);

  // Filters
  const [search, setSearch] = useState('');
  const [statusId, setStatusId] = useState('');
  const [city, setCity] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [pages, setPages] = useState(1);

  // Modal
  const [showModal, setShowModal] = useState(false);

  // Quick-add modals from table row
  const [quickOrder, setQuickOrder] = useState(null);   // { id, name }
  const [quickSC, setQuickSC] = useState(null);         // { id, name }

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mergeIds, setMergeIds] = useState(null); // [id1, id2] when merging

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, sort_by: sortBy, sort_dir: sortDir };
      if (search) params.search = search;
      if (statusId) params.status_id = statusId;
      if (city) params.city = city;

      const res = await api.get('/customers', { params });
      setCustomers(res.data.data);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusId, city, sortBy, sortDir]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    api.get('/statuses').then((r) => setStatuses(r.data)).catch(() => {});
  }, []);

  useEffect(() => { setPage(1); }, [search, statusId, city]);

  // Load column preferences
  useEffect(() => {
    api.get('/auth/me').then((r) => {
      const saved = r.data.preferences?.hidden_cols_customers;
      if (Array.isArray(saved)) setHiddenCols(new Set(saved));
    }).catch(() => {});
  }, []);

  // Close col picker on outside click
  useEffect(() => {
    if (!showColPicker) return;
    function handleClick(e) {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setShowColPicker(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showColPicker]);

  function toggleCol(key) {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      api.put('/auth/preferences', { hidden_cols_customers: [...next] }).catch(() => {});
      return next;
    });
  }

  function handleSort(col) {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
  }

  async function deleteCustomer(e, id) {
    e.stopPropagation();
    if (!window.confirm('למחוק לקוח זה? פעולה זו אינה ניתנת לביטול ותמחק את כל הנתונים הקשורים אליו.')) return;
    try {
      await api.delete(`/customers/${id}`);
      loadCustomers();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה במחיקת לקוח');
    }
  }

  async function changeCustomerStatus(customerId, newStatusId) {
    try {
      await api.put(`/customers/${customerId}/status`, { status_id: newStatusId, is_manual: true });
      loadCustomers();
    } catch {
      alert('שגיאה בעדכון סטטוס');
    }
  }

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === customers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(customers.map((c) => c.id)));
    }
  }

  async function deleteSelected() {
    if (!window.confirm(`למחוק ${selectedIds.size} לקוחות? פעולה זו אינה ניתנת לביטול.`)) return;
    try {
      await Promise.all([...selectedIds].map((id) => api.delete(`/customers/${id}`)));
      setSelectedIds(new Set());
      loadCustomers();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה במחיקת לקוחות');
    }
  }

  function startMerge() {
    const ids = [...selectedIds];
    if (ids.length !== 2) { alert('יש לבחור בדיוק 2 לקוחות למיזוג'); return; }
    setMergeIds(ids);
  }

  function exportCSV() {
    const selected = customers.filter((c) => selectedIds.has(c.id));
    const rows = [
      ['שם', 'טלפון', 'עיר', 'אימייל', 'סטטוס', 'הזמנות', 'שיחות', 'תאריך הצטרפות'],
      ...selected.map((c) => [
        c.name || '',
        c.primary_phone || '',
        c.city || '',
        c.email || '',
        c.status_name || '',
        c.orders_count || 0,
        c.calls_count || 0,
        new Date(c.created_at).toLocaleDateString('he-IL'),
      ]),
    ];
    const csv = '\uFEFF' + rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'customers.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  const lowerSearch = search.toLowerCase();
  const strippedSearch = search.replace(/-/g, '');
  const displayedCustomers = customers.filter(c => !search ||
    c.name?.toLowerCase().includes(lowerSearch) ||
    c.primary_phone?.replace(/-/g, '').includes(strippedSearch) ||
    c.city?.toLowerCase().includes(lowerSearch) ||
    c.email?.toLowerCase().includes(lowerSearch) ||
    c.notes?.toLowerCase().includes(lowerSearch)
  );

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="text-primary-600 dark:text-primary-400" size={22} />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">לקוחות</h1>
          <span className="text-sm text-gray-500 dark:text-gray-400">({total.toLocaleString()})</span>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700
                     text-white text-sm font-medium transition-colors"
        >
          <Plus size={16} />
          לקוח חדש
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="חיפוש לפי שם, טלפון, עיר, מייל, הערות..."
              className="w-full pr-9 pl-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                         bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <select
            value={statusId}
            onChange={(e) => setStatusId(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                       bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">כל הסטטוסים</option>
            {statuses.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="סינון לפי עיר..."
            className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                       bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />

          {(search || statusId || city) && (
            <button
              onClick={() => { setSearch(''); setStatusId(''); setCity(''); }}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X size={14} />
              נקה
            </button>
          )}

          {/* Column picker */}
          <div className="relative mr-auto" ref={colPickerRef}>
            <button
              onClick={() => setShowColPicker((v) => !v)}
              title="הצג/הסתר טורים"
              className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors
                ${showColPicker
                  ? 'bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:border-primary-700 dark:text-primary-400'
                  : 'bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'}`}
            >
              <SlidersHorizontal size={14} />
              טורים
              {hiddenCols.size > 0 && (
                <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[10px] font-bold bg-primary-600 text-white">
                  {hiddenCols.size}
                </span>
              )}
            </button>
            {showColPicker && (
              <div className="absolute left-0 top-full mt-1 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-2 min-w-[160px]">
                {CUSTOMER_COLS.map((col) => (
                  <label key={col.key} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-700 dark:text-gray-300 select-none">
                    <input
                      type="checkbox"
                      checked={!hiddenCols.has(col.key)}
                      onChange={() => toggleCol(col.key)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    {col.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
            {selectedIds.size} לקוחות נבחרו
          </span>
          <div className="flex gap-2 mr-auto">
            {selectedIds.size === 2 && (
              <button onClick={startMerge}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:border-primary-400 hover:text-primary-600 transition-colors">
                <GitMerge size={14} /> מזג
              </button>
            )}
            <button onClick={exportCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:border-primary-400 hover:text-primary-600 transition-colors">
              <Download size={14} /> ייצא CSV
            </button>
            {isAdmin && (
              <button onClick={deleteSelected}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                <Trash2 size={14} /> מחק
              </button>
            )}
            <button onClick={() => setSelectedIds(new Set())}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <X size={14} /> בטל בחירה
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <th className="px-3 py-3 w-10 text-center" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" className="rounded"
                    checked={displayedCustomers.length > 0 && selectedIds.size === displayedCustomers.length}
                    onChange={toggleSelectAll} />
                </th>
                <SortTh col="name" label="שם" sortBy={sortBy} sortDir={sortDir} onClick={handleSort} />
                {!hiddenCols.has('phone') && <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">טלפון</th>}
                {!hiddenCols.has('city') && <SortTh col="city" label="עיר" sortBy={sortBy} sortDir={sortDir} onClick={handleSort} />}
                <SortTh col="status" label="סטטוס" sortBy={sortBy} sortDir={sortDir} onClick={handleSort} />
                {!hiddenCols.has('interests') && <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">תחומי עניין</th>}
                {!hiddenCols.has('orders') && <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">הזמנות</th>}
                {!hiddenCols.has('service_calls') && <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">קריאות שירות</th>}
                {!hiddenCols.has('calls_count') && <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">שיחות</th>}
                {!hiddenCols.has('created_at') && <SortTh col="created_at" label="תאריך הצטרפות" sortBy={sortBy} sortDir={sortDir} onClick={handleSort} />}
                {isAdmin && <th className="px-3 py-3 w-10" />}
              </tr>
            </thead>
            <tbody>
              {loading && displayedCustomers.length === 0 ? (
                <tr><td colSpan={20} className="py-12 text-center"><div className="flex justify-center"><div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" /></div></td></tr>
              ) : displayedCustomers.length === 0 ? (
                <tr><td colSpan={20} className="py-12 text-center text-gray-400">אין לקוחות</td></tr>
              ) : (
                displayedCustomers.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/customers/${c.id}`)}
                    className="border-b border-gray-100 dark:border-gray-700/50 hover:brightness-95 dark:hover:brightness-110 cursor-pointer"
                    style={(c.status_color && c.status_name !== 'מתעניין') ? { backgroundColor: c.status_color + '18' } : undefined}
                  >
                    <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="rounded"
                        checked={selectedIds.has(c.id)}
                        onChange={() => toggleSelect(c.id)} />
                    </td>
                    <td className="px-4 py-3 text-center font-medium text-gray-900 dark:text-white">
                      {c.name || <span className="text-gray-400 italic">ללא שם</span>}
                    </td>
                    {!hiddenCols.has('phone') && (
                      <td className="px-4 py-3 text-center font-mono text-gray-600 dark:text-gray-300 text-xs" dir="ltr">
                        {c.primary_phone || '—'}
                      </td>
                    )}
                    {!hiddenCols.has('city') && (
                      <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-300">{c.city || '—'}</td>
                    )}
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={c.status_id || ''}
                        onChange={(e) => changeCustomerStatus(c.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs px-2 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 font-medium"
                        style={{ borderColor: c.status_color || '#6B7280', color: c.status_color || '#6B7280' }}
                      >
                        <option value="">— ללא סטטוס —</option>
                        {statuses.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </td>
                    {!hiddenCols.has('interests') && (
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-wrap gap-1 justify-center">
                          {Array.isArray(c.interests) && c.interests.map((name) => (
                            <span
                              key={name}
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium
                                         bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 whitespace-nowrap"
                            >
                              {name}
                            </span>
                          ))}
                          {(!c.interests || c.interests.length === 0) && (
                            <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                          )}
                        </div>
                      </td>
                    )}
                    {!hiddenCols.has('orders') && (
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setQuickOrder({ id: c.id, name: c.name })}
                          title="הוסף הזמנה חדשה"
                          className="group inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-sm font-medium
                                     text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20
                                     border border-transparent hover:border-blue-200 dark:hover:border-blue-800 transition-colors"
                        >
                          {c.orders_count || 0}
                          <Plus size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </td>
                    )}
                    {!hiddenCols.has('service_calls') && (
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setQuickSC({ id: c.id, name: c.name })}
                          title="הוסף קריאת שירות חדשה"
                          className="group inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-sm font-medium
                                     text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20
                                     border border-transparent hover:border-emerald-200 dark:hover:border-emerald-800 transition-colors"
                        >
                          {c.service_calls_count || 0}
                          <Plus size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </td>
                    )}
                    {!hiddenCols.has('calls_count') && (
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-center text-sm font-medium">{c.calls_count || 0}</td>
                    )}
                    {!hiddenCols.has('created_at') && (
                      <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500 text-xs">
                        {new Date(c.created_at).toLocaleDateString('he-IL')}
                      </td>
                    )}
                    {isAdmin && (
                      <td className="px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => deleteCustomer(e, c.id)}
                          title="מחק לקוח"
                          className="p-1.5 rounded text-gray-300 dark:text-gray-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm text-gray-500">עמוד {page} מתוך {pages}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1.5 text-sm rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700">הקודם</button>
              <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages}
                className="px-3 py-1.5 text-sm rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700">הבא</button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <CustomerModal
          statuses={statuses}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); loadCustomers(); }}
        />
      )}

      {mergeIds && (
        <CustomerMergeModal
          ids={mergeIds}
          onClose={() => setMergeIds(null)}
          onMerged={() => { setMergeIds(null); setSelectedIds(new Set()); loadCustomers(); }}
        />
      )}

      {quickOrder && (
        <OrderModal
          customerId={quickOrder.id}
          customerName={quickOrder.name}
          onClose={() => setQuickOrder(null)}
          onSaved={() => { setQuickOrder(null); loadCustomers(); }}
        />
      )}

      {quickSC && (
        <ServiceCallModal
          customerId={quickSC.id}
          onClose={() => setQuickSC(null)}
          onSaved={() => { setQuickSC(null); loadCustomers(); }}
        />
      )}
    </div>
  );
}

function SortTh({ col, label, sortBy, sortDir, onClick }) {
  const active = sortBy === col;
  return (
    <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => onClick(col)}>
      <span className="flex items-center gap-1 justify-center">
        {label}
        {active ? (sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />) : <ChevronDown size={14} className="opacity-30" />}
      </span>
    </th>
  );
}
